import React, { useEffect, useRef, useMemo } from 'react';

import validate from '@vlabs/shared/validators';
import { Control, Divider, SettingsItemWrapper } from '@vlabs/uikit';
import type { TOption } from '@vlabs/uikit/types';
import { useFormContext, useWatch } from 'react-hook-form';

import { ControllableValueSelect } from '@vlabs/monokit/shared/ui/value-select';

import { aggregatorOptions, targetOptions } from '../constants';
import st from './shared.module.sass';
import { DiagramTypeSelect } from '../controls/DiagramTypeSelect';
import { useChartFormTranslation } from '../i18n';
import type { ExtendedUseFormReturn } from '../types';

const aggregatorAvailability = {
  event_id: ['count'],
  external_id: ['count'],
  handler_id: ['count'],
  stream_id: ['count'],
  source: ['count'],
  top_matching_candidates_label: ['count'],
  top_similar_object_id: ['count'],
  top_similar_external_id: ['count'],
  top_similar_object_similarity: ['count', 'max', 'min', 'avg'],
  create_time: ['count', 'max', 'min'],
  end_time: ['count', 'max', 'min'],
  face_id: ['count'],
  gender: ['count', 'max', 'min', 'avg'],
  age: ['count', 'max', 'min', 'avg'],
  emotion: ['count', 'max', 'min', 'avg'],
  mask: ['count', 'max', 'min', 'avg'],
  liveness: ['count', 'max', 'min', 'avg'],
  deepfake: ['count', 'max', 'min', 'avg'],
  user_data: ['count'],
  tags: ['count', 'max', 'min'],
  city: ['count'],
  area: ['count'],
  district: ['count'],
  street: ['count'],
  house_number: ['count'],
  geo_position: ['count'],
  track_id: ['count'],
  apparent_age: ['count', 'max', 'min', 'avg'],
  apparent_gender: ['count', 'max', 'min', 'avg'],
  sleeve_length: ['count'],
  headwear_state: ['count', 'max', 'min', 'avg'],
  headwear_apparent_color: ['count'],
  upper_clothing_colors: ['count'],
  lower_garment_colors: ['count'],
  lower_garment_type: ['count'],
  shoes_apparent_color: ['count'],
  backpack_state: ['count', 'max', 'min', 'avg'],
  meta: ['count', 'max', 'min', 'avg'],
  lists: ['count'],
};

const filterAggregatorOptions = (columnValue: keyof typeof aggregatorAvailability): TOption[] => {
  const availableAggregators: string[] = aggregatorAvailability[columnValue] ?? [];
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return aggregatorOptions.raw.filter((option: TOption) => availableAggregators.includes(option.value));
};

export function CommonParamsForm(): React.JSX.Element {
  const { t } = useChartFormTranslation();
  const {
    control, register, formState: { errors }, setValue, resetState,
  } = useFormContext() as ExtendedUseFormReturn;
  const selectedColumn = useWatch({ name: 'getEventStats.targets.[0].column' });
  const previousSelectedColumnRef = useRef(selectedColumn);

  const availableAggregatorOptions = useMemo(() => {
    return filterAggregatorOptions(selectedColumn);
  }, [selectedColumn]);

  useEffect(() => {
    if (resetState.current.commonForm) {
      resetState.current.commonForm = false;
      return;
    }

    if (previousSelectedColumnRef?.current === selectedColumn) return;
    const options = availableAggregatorOptions.length === 1 ? availableAggregatorOptions[0] : null;
    setValue('getEventStats.targets.[0].aggregator', options);

    previousSelectedColumnRef.current = selectedColumn;
  }, [availableAggregatorOptions, resetState, selectedColumn, setValue]);

  return (
    <React.Fragment>
      <Divider />
      <div className={st.ColorPanel}>
        <SettingsItemWrapper
          contentClassName={st.SettingsItem__Content}
          title={t('form.common.name.label')}
          titleClassName={st.SettingsItem__Title}
        >
          <Control.Input
            {...register('getTemplateStats.name')}
            errors={errors}
            id="getTemplateStats.name"
          />
        </SettingsItemWrapper>

        <SettingsItemWrapper
          contentClassName={st.SettingsItem__Content}
          title={t('form.common.diagram_type.label')}
          titleClassName={st.SettingsItem__Title}
        >
          <DiagramTypeSelect
            control={control}
            name="getTemplateStats.diagramType"
          />
        </SettingsItemWrapper>

        <SettingsItemWrapper
          contentClassName={st.SettingsItem__Content}
          data-tooltip-content={t('form.common.main_targets.column.tooltip') as unknown as string}
          title={t('form.common.main_targets.column.label')}
          titleClassName={st.SettingsItem__Title}
        >
          <ControllableValueSelect
            control={control}
            id="getEventStats.targets.[0].column"
            name="getEventStats.targets.[0].column"
            options={targetOptions.raw}
          />
        </SettingsItemWrapper>

        <SettingsItemWrapper
          contentClassName={st.SettingsItem__Content}
          data-tooltip-content={t('form.common.aggregator.tooltip') as unknown as string}
          title={t('form.common.aggregator.label')}
          titleClassName={st.SettingsItem__Title}
        >
          <ControllableValueSelect
            control={control}
            errors={errors}
            id="getEventStats.targets.[0].aggregator"
            name="getEventStats.targets.[0].aggregator"
            options={availableAggregatorOptions}
            rules={{ required: validate.required() }}
          />
        </SettingsItemWrapper>
      </div>
    </React.Fragment>
  );
}
